import { useCallback, useEffect, useState, useRef } from 'react';
import {
	createRequest,
	deleteRequest as deleteRequestApi,
	fetchAllRequests,
	fetchRequestById,
	updateRequest as updateRequestApi,
	updateRequestStatus as updateRequestStatusApi,
} from '../api';
import type { RequestUpsertInput } from '../types';
import type { RequestWithRelations } from '@cleanpro/common-ui';
import { Cache, RequestDeduplicator, useRealtimeSubscription } from '@cleanpro/common-ui';

const sortByCreatedAtDesc = (a: RequestWithRelations, b: RequestWithRelations) =>
	new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const mergeRequestIntoState = (
	collection: RequestWithRelations[],
	next: RequestWithRelations
) => {
	const index = collection.findIndex((request) => request.id === next.id);
	if (index === -1) {
		return [next, ...collection].sort(sortByCreatedAtDesc);
	}

	const updated = [...collection];
	updated[index] = { ...updated[index], ...next };
	return updated.sort(sortByCreatedAtDesc);
};

// Create cache and deduplicator instances (shared across component instances)
const requestsCache = new Cache<RequestWithRelations[]>(5 * 60 * 1000); // 5 minutes TTL
const requestByIdCache = new Cache<RequestWithRelations>(5 * 60 * 1000); // 5 minutes TTL
const deduplicator = new RequestDeduplicator();

export const useRequestsData = () => {
	const [requests, setRequests] = useState<RequestWithRelations[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const lastFetchTimeRef = useRef<number>(0);

	const refreshRequests = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const data = await fetchAllRequests();
			setRequests(data);
			setError(null);
		} catch (err) {
			console.error('Error fetching requests:', err);
			const message = err instanceof Error ? err.message : 'Failed to load requests';
			setError(message);
			throw err;
		} finally {
			setLoading(false);
		}

		// Deduplicate concurrent requests
		return deduplicator.deduplicate('fetch-all-requests', async () => {
			try {
				setLoading(true);
				setError(null);
				const data = await fetchAllRequests();
				console.log('✅ Requests fetched successfully:', data.length, 'requests');
				console.log('🔍 About to set requests data:', data);
				
				// Update cache
				requestsCache.set('all-requests', data, { tags: ['requests'] });
				lastFetchTimeRef.current = Date.now();
				
				setRequests(data);
				setError(null);
				console.log('📊 Requests state should be updated');
			} catch (err) {
				console.error('❌ Error fetching requests:', err);
				const message = err instanceof Error ? err.message : 'Failed to load requests';
				setError(message);
				throw err;
			} finally {
				console.log('🔄 Setting loading to false');
				setLoading(false);
			}
		});
	}, []);

	useEffect(() => {
		console.log('🎬 useRequestsData useEffect triggered - calling refreshRequests');
		refreshRequests().catch(() => {
			console.log('💥 refreshRequests failed in useEffect');
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only run once on mount

	// Setup real-time subscription
	useRealtimeSubscription({
		table: 'requests',
		filter: 'status=in.(pending,assessed,quoted)',
		callbacks: {
			onInsert: (payload) => {
				// Fetch the full request with relations
				fetchRequestById((payload.new as { id: string }).id)
					.then((newRequest) => {
						if (newRequest) {
							setRequests((prev) => mergeRequestIntoState(prev, newRequest));
						}
					})
					.catch((err) => {
						console.error('Error fetching new request:', err);
					});
			},
			onUpdate: (payload) => {
				console.log('[Realtime] Request updated:', payload.new);
				// Fetch the full request with relations
				fetchRequestById((payload.new as { id: string }).id)
					.then((updatedRequest) => {
						if (updatedRequest) {
							setRequests((prev) => mergeRequestIntoState(prev, updatedRequest));
						}
					})
					.catch((err) => {
						console.error('Error fetching updated request:', err);
					});
			},
			onDelete: (payload) => {
				console.log('[Realtime] Request deleted:', payload.old);
				setRequests((prev) => prev.filter((request) => request.id !== (payload.old as { id: string }).id));
			},
		},
	});

	const getRequestById = useCallback(
		async (requestId: string) => {
			// Check in-memory state first
			const cached = requests.find((request) => request.id === requestId);
			if (cached) return cached;

			// Check cache
			const cachedRequest = requestByIdCache.get(requestId);
			if (cachedRequest) {
				console.log('✅ Using cached request:', requestId);
				setRequests((prev) => mergeRequestIntoState(prev, cachedRequest));
				return cachedRequest;
			}

			// Deduplicate concurrent requests for the same ID
			return deduplicator.deduplicate(`fetch-request-${requestId}`, async () => {
				try {
					const remote = await fetchRequestById(requestId);
					if (remote) {
						// Update cache
						requestByIdCache.set(requestId, remote, { tags: ['requests', `request-${requestId}`] });
						setRequests((prev) => mergeRequestIntoState(prev, remote));
						return remote;
					}
					return remote;
				} catch (err) {
					const message = err instanceof Error ? err.message : 'Failed to load request';
					setError(message);
					throw err;
				}
			});
		},
		[requests]
	);

	const addRequest = useCallback(async (input: RequestUpsertInput) => {
		const created = await createRequest(input);
		setRequests((prev) => mergeRequestIntoState(prev, created));
		
		// Invalidate cache after mutation
		requestsCache.invalidateByTag('requests');
		requestByIdCache.set(created.id, created, { tags: ['requests', `request-${created.id}`] });
	}, []);

	const updateRequest = useCallback(
		async (requestId: string, requestData: Partial<RequestUpsertInput>) => {
			await updateRequestApi(requestId, requestData);
			setRequests((prev) =>
					prev.map((request) => {
						if (request.id !== requestId) return request;

						const next: RequestWithRelations = {
							...request,
							customerId: requestData.customerId ?? request.customerId,
							serviceId: requestData.serviceId ?? request.serviceId,
							propertyId: requestData.propertyId ?? request.propertyId,
							requestedDateTime: requestData.requestedDateTime ?? request.requestedDateTime ?? undefined,
							notes: requestData.notes ?? request.notes,
							status: requestData.status ?? request.status,
							urgency: requestData.urgency ?? request.urgency,
							referral: requestData.referral ?? request.referral,
							estimatedDuration:
								requestData.estimatedDuration ?? request.estimatedDuration ?? undefined,
							accessInstructions:
								requestData.accessInstructions ?? request.accessInstructions ?? undefined,
							updatedAt: new Date().toISOString(),
						};

						return next;
					})
			);
			
			// Invalidate cache after mutation
			requestsCache.invalidateByTag('requests');
			requestByIdCache.invalidateByTag(`request-${requestId}`);
		},
		[]
	);

	const updateRequestStatus = useCallback(
		async (requestId: string, status: RequestWithRelations['status']) => {
			await updateRequestStatusApi(requestId, status);
			setRequests((prev) =>
				prev.map((request) =>
					request.id === requestId
						? { ...request, status, updatedAt: new Date().toISOString() }
						: request
				)
			);
			
			// Invalidate cache after mutation
			requestsCache.invalidateByTag('requests');
			requestByIdCache.invalidateByTag(`request-${requestId}`);
		},
		[]
	);

	const deleteRequest = useCallback(async (requestId: string) => {
		await deleteRequestApi(requestId);
		setRequests((prev) => prev.filter((request) => request.id !== requestId));
		
		// Invalidate cache after mutation
		requestsCache.invalidateByTag('requests');
		requestByIdCache.invalidateByTag(`request-${requestId}`);
	}, []);

	return {
		requests,
		loading,
		error,
		refreshRequests,
		getRequestById,
		addRequest,
		updateRequest,
		updateRequestStatus,
		deleteRequest,
	};
};
